// Fixed ExpenseDetailPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import axiosInstance from '../utils/axiosConfig'; 

interface StaffMember {
  _id: string;
  name: string;
  email: string;
}

interface Expense {
  _id: string;
  staffId: StaffMember | string; // Could be populated or just ID
  category: string;
  businessName: string;
  amount: number;
  description: string;
  date: string;
  status: string;
  method: string;
  imcCode: string;
  vat: boolean;
  vatAmount?: number;
  receiptPhoto?: string; // This is the GCS URL
  receiptUrl?: string;   // Alternative field name
  attachments?: string[]; // Could be array
}

const ExpenseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [expense, setExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);

  useEffect(() => {
    const fetchExpense = async () => {
      try {
        // Make sure to populate staffId
        const response = await axiosInstance.get<Expense>(`/api/expenses/${id}?populate=staffId`);
        console.log('Expense data:', response.data); // Debug log
        setExpense(response.data);
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching expense:', err);
        setError(err.response?.data?.message || 'Failed to fetch expense details');
        setLoading(false);
      }
    };

    if (id) {
      fetchExpense();
    }
  }, [id]);

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/api/expenses/${id}`);
      navigate('/admin/expenses');
    } catch (err: any) {
      console.error('Error deleting expense:', err);
      setError(err.response?.data?.message || 'Failed to delete expense');
    }
  };

  const getStaffName = (): string => {
    if (!expense?.staffId) return 'Unknown';
    
    // Check if staffId is populated (object) or just an ID (string)
    if (typeof expense.staffId === 'object' && expense.staffId.name) {
      return expense.staffId.name;
    }
    
    // If it's just a string ID, show the ID
    return typeof expense.staffId === 'string' ? `Staff ID: ${expense.staffId}` : 'Unknown';
  };

  const getReceiptImageUrl = (): string | null => {
    if (!expense) return null;
    
    // Check multiple possible field names for the receipt
    const possibleFields = [
      expense.receiptPhoto,
      expense.receiptUrl,
      expense.attachments?.[0] // If it's an array, take first
    ];
    
    for (const field of possibleFields) {
      if (field && typeof field === 'string') {
        console.log('Found receipt URL:', field); // Debug log
        return field;
      }
    }
    
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading expense details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!expense) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        No expense found
      </Alert>
    );
  }

  const receiptUrl = getReceiptImageUrl();

  return (
    <Paper style={{ padding: '20px', margin: '20px 0' }}>
      <Typography variant="h4" gutterBottom>Expense Detail</Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Expense Information</Typography>
              <Typography><strong>Employee:</strong> {getStaffName()}</Typography>
              <Typography><strong>Category:</strong> {expense.category}</Typography>
              <Typography><strong>Business Name:</strong> {expense.businessName}</Typography>
              <Typography><strong>Amount:</strong> €{expense.amount.toFixed(2)}</Typography>
              <Typography><strong>Description:</strong> {expense.description}</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Additional Details</Typography>
              <Typography><strong>Date:</strong> {new Date(expense.date).toLocaleDateString('en-IE')}</Typography>
              <Typography><strong>Status:</strong> {expense.status}</Typography>
              <Typography><strong>Method:</strong> {expense.method}</Typography>
              <Typography><strong>IMC Code:</strong> {expense.imcCode}</Typography>
              <Typography><strong>VAT:</strong> {expense.vat ? 'Yes' : 'No'}</Typography>
              {expense.vat && expense.vatAmount && (
                <Typography><strong>VAT Amount:</strong> €{expense.vatAmount.toFixed(2)}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Receipt Photo Section */}
        {receiptUrl && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Receipt Photo</Typography>
                {!imageError ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <img 
                      src={receiptUrl} 
                      alt="Receipt" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '600px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                      onError={() => {
                        console.error('Failed to load image:', receiptUrl);
                        setImageError(true);
                      }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Receipt URL: {receiptUrl}
                    </Typography>
                  </Box>
                ) : (
                  <Alert severity="error">
                    Failed to load receipt image. URL: {receiptUrl}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Debug Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Debug Information</Typography>
              <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                {JSON.stringify(expense, null, 2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate(`/admin/expenses/${id}/edit`)}
        >
          Edit
        </Button>
        <Button 
          variant="contained" 
          color="error" 
          onClick={() => setOpenDialog(true)}
        >
          Delete
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/admin/expenses')}
        >
          Back to List
        </Button>
      </Box>
      
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this expense? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ExpenseDetailPage;